import { AddModalPresenter } from "./AddModalPresenter";

import { useRef, useState } from "react";
import { useQueryClient } from "react-query";
import {
  CustomError,
  ValidateMsg,
} from "api/interfaces/commonInterface.interface";
import useStatusMutation from "hook/useStatusMutation";
import useApiError from "hook/useApiError";
import {
  CreateFirmwarePackageRequest,
  FIRMWARE_UPLOAD_STATUS,
  FirmwarePackageListResult,
  FirmwarePackageUploadStatusResponse,
  UpdateFirmwarePackageRequest,
} from "api/interfaces/settingsInterface.interface";
import {
  postFirmwarePackage,
  putFirmwareUpload,
  postFirmwareUploadStatus,
  putFirmwarePackage,
} from "api/settingAPI";
import { useQuery } from "react-query";
import { computeFileCrc32Hex, createEditPayload } from "utils/firmwareUtil";
import { checkEmpty, toTrim } from "utils/functions";

interface Props {
  selectedRow?: FirmwarePackageListResult | undefined;
  onModal: () => void;
  onDownloadFile: (fileUrl: string) => void;
  validateMsg?: ValidateMsg;
  onDelete?: (id: string) => void;
  isLoading?: boolean;
}

const initialForm: CreateFirmwarePackageRequest = {
  version: "",
  recorderType: "COVA",
  model: "DW_COVA4xT_DW_COVA8xT",
  fileName: "",
  fileSize: 0,
  checksum: "",
  releaseNote: "",
  isActive: false,
};

export const firmwareErrorKey = "label.setting.firmware.errorCode";

export function AddModalContainer(props: Props): JSX.Element {
  const queryClient = useQueryClient();
  const [id, setId] = useState<string>("");
  const [uploadStatus, setUploadStatus] =
    useState<FIRMWARE_UPLOAD_STATUS | null>(null);
  const uploadFormRef = useRef<HTMLInputElement>(null);
  const {
    lang,
    notifySuccess,
    createCustomError,
    intlString,
    handleErrorNotify,
  } = useApiError();

  const [isPermanently, setIsPermanently] = useState<boolean>(true);
  const [validateMsg, setValidateMsg] = useState<ValidateMsg>({
    title: "",
    message: "",
  });

  const [form, setForm] = useState<CreateFirmwarePackageRequest>(() =>
    props.selectedRow !== undefined ? props.selectedRow : initialForm
  );

  const [file, setFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const onUpdateFailed = (err: CustomError) => {
    setIsLoading(false);
    handleErrorNotify(
      err,
      lang.label.setting.firmware.notify.update.fail,
      firmwareErrorKey
    );
  };

  useQuery(
    ["checkUploadStatus", id],
    () => postFirmwareUploadStatus({ id: id as string }),
    {
      enabled: uploadStatus === FIRMWARE_UPLOAD_STATUS.PRESIGNED && id !== "",
      refetchInterval: 1000,
      onSuccess: (res: FirmwarePackageUploadStatusResponse) => {
        if (
          res.error !== 0 ||
          !res.result ||
          res.result.uploadStatus === FIRMWARE_UPLOAD_STATUS.FAILED
        ) {
          setUploadStatus(FIRMWARE_UPLOAD_STATUS.FAILED);
          const error = createCustomError(
            res,
            lang.label.setting.firmware.notify.add.fail
          );
          onUpdateFailed(error);
          return;
        }
        if (res.result.uploadStatus === FIRMWARE_UPLOAD_STATUS.UPLOADED) {
          setUploadStatus(FIRMWARE_UPLOAD_STATUS.UPLOADED);
          notifySuccess(lang.label.setting.firmware.notify.add.success);
          props.onModal();
          queryClient.invalidateQueries("getDwFirmwareList");
          setIsLoading(false);
        }
      },
      onError: (err: CustomError) => {
        setUploadStatus(FIRMWARE_UPLOAD_STATUS.FAILED);
        onUpdateFailed(err);
      },
    }
  );
  const onChange = (e: any) => {
    let {
      target: { value, name },
    } = e;

    setForm((prev) => {
      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const mutationPostFirmwareUpload = useStatusMutation(putFirmwareUpload, {
    onSuccess: (res) => {
      setUploadStatus(FIRMWARE_UPLOAD_STATUS.PRESIGNED);
    },
    onError: onUpdateFailed,
  });

  const mutationCreateDwFirmware = useStatusMutation(postFirmwarePackage, {
    onSuccess: (res) => {
      if (res.error !== 0) {
        const error = createCustomError(
          res,
          lang.label.setting.firmware.notify.add.fail
        );
        onUpdateFailed(error);
        return;
      }
      if (file === null) {
        throw new Error(lang.label.setting.firmware.notify.add.fail);
      }
      mutationPostFirmwareUpload.mutate({
        presignedUrl: res.result.presignedUrl,
        file: file,
      });
      setId(res.result.id);
    },
    onError: onUpdateFailed,
  });

  const mutationUpdateDwFirmware = useStatusMutation(putFirmwarePackage, {
    onSuccess: (res) => {
      if (res.error !== 0) {
        const error = createCustomError(
          res,
          lang.label.setting.firmware.notify.update.fail
        );
        onUpdateFailed(error);
        return;
      }
      if (!!file) {
        mutationPostFirmwareUpload.mutate({
          presignedUrl: res.result.presignedUrl,
          file: file,
        });
        setId(res.result.id);
        return;
      }
      notifySuccess(lang.label.setting.firmware.notify.update.success);
      queryClient.invalidateQueries("getDwFirmwareList");
      setIsLoading(false);
      props.onModal();
    },
    onError: onUpdateFailed,
  });
  const onValidate = (form: CreateFirmwarePackageRequest) => {
    if (checkEmpty(form.version)) {
      setValidateMsg((info) => {
        return {
          ...info,
          version: intlString(lang.validateMsg.firmware.version),
        };
      });
      return true;
    }
    if (file === null && props.selectedRow === undefined) {
      setValidateMsg((info) => {
        return {
          ...info,
          file: intlString(lang.validateMsg.firmware.file),
        };
      });
      return true;
    }
    if (checkEmpty(form.releaseNote)) {
      setValidateMsg((info) => {
        return {
          ...info,
          releaseNote: intlString(lang.validateMsg.firmware.releaseNote),
        };
      });
      return true;
    }
    return false;
  };

  const onSave = async () => {
    setValidateMsg((info) => {
      return { title: "", message: "" };
    });

    if (onValidate(form)) {
      return;
    }
    const isEdit = props.selectedRow !== undefined;
    if (file === null && !isEdit) {
      return;
    }
    setIsLoading(true);
    let checksum = props.selectedRow?.checksum ?? "";
    let fileName = props.selectedRow?.fileName ?? "";
    let fileSize = props.selectedRow?.fileSize ?? 0;
    if (file) {
      checksum = await computeFileCrc32Hex(file);
      fileName = file.name;
      fileSize = file.size;
    }
    const payload: CreateFirmwarePackageRequest = {
      ...form,
      version: toTrim(form.version),
      releaseNote: toTrim(form.releaseNote),
      fileName,
      fileSize,
      checksum,
    };

    if (isEdit && props.selectedRow) {
      let editPayload = createEditPayload(payload, props.selectedRow);
      if (file) {
        editPayload.checksum = checksum;
        editPayload.fileName = fileName;
        editPayload.fileSize = fileSize;
      }
      mutationUpdateDwFirmware.mutate({
        id: props.selectedRow.id,
        payload: editPayload as UpdateFirmwarePackageRequest,
      });
      return;
    }
    if (!mutationCreateDwFirmware.idle) return;
    mutationCreateDwFirmware.mutate(payload);
  };

  const onChangePermanent = (e: any) => {
    setIsPermanently(!isPermanently);
  };

  const onUpload = (e: any) => {
    let currentFile = e.target.files[0];
    if (
      currentFile &&
      currentFile.name.substring(currentFile.name.lastIndexOf(".") + 1) !==
        "pkg"
    ) {
      setValidateMsg((info) => {
        return {
          ...info,
          file: intlString(lang.label.setting.firmware.notify.add.file.format),
        };
      });
      if (uploadFormRef.current) {
        uploadFormRef.current.value = "";
      }
      setFile(null);
      return;
    }
    setFile(currentFile);
  };

  return (
    <AddModalPresenter
      onModal={props.onModal}
      onChange={onChange}
      form={form}
      onDelete={props.onDelete}
      validateMsg={validateMsg}
      isPermanently={isPermanently}
      onChangePermanent={onChangePermanent}
      onSave={onSave}
      onUpload={onUpload}
      onDownloadFile={props.onDownloadFile}
      selectedRow={props.selectedRow}
      isLoading={isLoading || (props.isLoading ?? false)}
      uploadFormRef={uploadFormRef}
    />
  );
}
